import { useMemo, type CSSProperties } from 'react';
import { BUTTON_PRESET_CONFIGS } from './buttonPresetConfigs';
import {
  type ButtonContainerVisualProps,
  resolveButtonContainerVisualProps,
} from './buttonContainerVisualProps';
import { resolveIsInteractive } from './buttonInteraction';
import {
  type ButtonContentModel,
  type ButtonLayoutModel,
  type ButtonRenderModel,
  type ButtonStyleModel,
  type NormalizedCbuttonProps,
  normalizeButtonProps,
  type ResolvedCbuttonProps,
  resolveButtonContentModel,
  resolveButtonLayoutModel,
  resolveButtonRenderModel,
  resolveButtonStyleModel,
} from './buttonRenderModel';
import {
  resolveButtonStyle,
  resolveContainerStyle,
  resolveCurrentStateContainerStyle,
} from './buttonStyles';
import {
  resolveButtonVisualState,
  type ButtonVisualState,
} from './buttonVisualState';
import {
  type ButtonClickInvestigationHandlers,
  useButtonClickInvestigation,
} from './useButtonClickInvestigation';
import {
  type ButtonPressState,
  useButtonPressState,
} from './useButtonPressState';

export function useButtonRenderModel(
  props: ResolvedCbuttonProps,
): ButtonRenderModel {
  const {
    label,
    ariaLabel,
    icon,
    iconSize,
    iconOpacity,
    iconStyle,
    iconColor,
    iconPosition,
    textSize,
    textWeight,
    textColor,
    children,
    beforeContent,
    glowLayer,
    preset,
    containerPreset,
    radius,
    padding,
    height,
    gap,
    borderColor,
    borderOpacity,
    backgroundOpacity,
    backdropBlur,
    onClick,
    href,
    onMouseEnter,
    onMouseLeave,
    disabled,
    disableHoverEffects,
    disableClickMotion,
    isLoading,
    containerStyle,
    stateContainerStyle,
    preserveStyle,
    className,
    style,
  } = props;
  const normalizedProps = useMemo<NormalizedCbuttonProps>(
    (): NormalizedCbuttonProps =>
      normalizeButtonProps({
        label,
        ariaLabel,
        icon,
        iconSize,
        iconOpacity,
        iconStyle,
        iconColor,
        iconPosition,
        textSize,
        textWeight,
        textColor,
        children,
        beforeContent,
        glowLayer,
        preset,
        containerPreset,
        radius,
        padding,
        height,
        gap,
        borderColor,
        borderOpacity,
        backgroundOpacity,
        backdropBlur,
        onClick,
        href,
        onMouseEnter,
        onMouseLeave,
        disabled,
        disableHoverEffects,
        disableClickMotion,
        isLoading,
        containerStyle,
        stateContainerStyle,
        preserveStyle,
        className,
        style,
      }),
    [
      ariaLabel,
      backdropBlur,
      backgroundOpacity,
      beforeContent,
      borderColor,
      borderOpacity,
      children,
      className,
      containerPreset,
      containerStyle,
      disableClickMotion,
      disableHoverEffects,
      disabled,
      gap,
      glowLayer,
      height,
      href,
      icon,
      iconColor,
      iconOpacity,
      iconPosition,
      iconSize,
      iconStyle,
      isLoading,
      label,
      onClick,
      onMouseEnter,
      onMouseLeave,
      padding,
      preset,
      preserveStyle,
      radius,
      stateContainerStyle,
      style,
      textColor,
      textSize,
      textWeight,
    ],
  );
  const normalizedIconSize = normalizedProps.iconSize;
  const normalizedIconPosition = normalizedProps.iconPosition;
  const normalizedPreset = normalizedProps.preset;
  const normalizedRadius = normalizedProps.radius;
  const normalizedGap = normalizedProps.gap;
  const normalizedDisabled = normalizedProps.disabled;
  const normalizedDisableHoverEffects = normalizedProps.disableHoverEffects;
  const normalizedIsLoading = normalizedProps.isLoading;
  const config = BUTTON_PRESET_CONFIGS[normalizedPreset];
  const isInteractive = resolveIsInteractive({
    onClick,
    href,
    disabled: normalizedDisabled,
  });
  const unresolvedPressState = useButtonPressState({
    isInteractive,
    disableHoverEffects: normalizedDisableHoverEffects,
    onMouseEnter,
    onMouseLeave,
  });
  const {
    isHovered,
    isPressed,
    handleTouchStart,
    handleTouchEnd,
    handleTouchCancel,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
  } = unresolvedPressState;
  const pressState = useMemo<ButtonPressState>(
    (): ButtonPressState => ({
      isHovered,
      isPressed,
      handleTouchStart,
      handleTouchEnd,
      handleTouchCancel,
      handleMouseEnter,
      handleMouseLeave,
      handleMouseDown,
      handleMouseUp,
    }),
    [
      handleMouseDown,
      handleMouseEnter,
      handleMouseLeave,
      handleMouseUp,
      handleTouchCancel,
      handleTouchEnd,
      handleTouchStart,
      isHovered,
      isPressed,
    ],
  );
  const unresolvedClickInvestigationHandlers = useButtonClickInvestigation({
    isInteractive,
    disabled: normalizedDisabled,
    isLoading: normalizedIsLoading,
    onClick,
    preset: normalizedPreset,
    label,
  });
  const {
    containerOnClick,
    handleClickInvestigationCapture,
    handlePointerDownInvestigationCapture,
  } = unresolvedClickInvestigationHandlers;
  const clickInvestigationHandlers = useMemo<ButtonClickInvestigationHandlers>(
    (): ButtonClickInvestigationHandlers => ({
      containerOnClick,
      handleClickInvestigationCapture,
      handlePointerDownInvestigationCapture,
    }),
    [
      containerOnClick,
      handleClickInvestigationCapture,
      handlePointerDownInvestigationCapture,
    ],
  );
  const visualState = useMemo<ButtonVisualState>(
    (): ButtonVisualState =>
      resolveButtonVisualState({
        config,
        preset: normalizedPreset,
        isHovered,
        isPressed,
        disableHoverEffects: normalizedDisableHoverEffects,
        textWeight,
      }),
    [
      config,
      isHovered,
      isPressed,
      normalizedDisableHoverEffects,
      normalizedPreset,
      textWeight,
    ],
  );
  const visualTextWeight = visualState.textWeight;
  const contentModel = useMemo<ButtonContentModel>(
    (): ButtonContentModel =>
      resolveButtonContentModel({
        icon,
        iconSize: normalizedIconSize,
        iconColor,
        iconOpacity,
        iconStyle,
        iconPosition: normalizedIconPosition,
        label,
        textSize,
        textWeight: visualTextWeight,
        textColor,
        children,
        config,
      }),
    [
      children,
      config,
      icon,
      iconColor,
      iconOpacity,
      iconStyle,
      label,
      normalizedIconPosition,
      normalizedIconSize,
      textColor,
      textSize,
      visualTextWeight,
    ],
  );
  const configBorderWidth = config.borderWidth;
  const layoutModel = useMemo<ButtonLayoutModel>(
    (): ButtonLayoutModel =>
      resolveButtonLayoutModel({
        padding,
        preset: normalizedPreset,
        iconSize: normalizedIconSize,
        height,
        disableHoverEffects: normalizedDisableHoverEffects,
        disableClickMotion,
        borderWidth: configBorderWidth,
        isInteractive,
        contentModel,
      }),
    [
      configBorderWidth,
      contentModel,
      disableClickMotion,
      height,
      isInteractive,
      normalizedDisableHoverEffects,
      normalizedIconSize,
      normalizedPreset,
      padding,
    ],
  );
  const buttonStyle = useMemo<CSSProperties>(
    (): CSSProperties =>
      resolveButtonStyle({
        disabled: normalizedDisabled,
        disableHoverEffects: normalizedDisableHoverEffects,
        isInteractive,
        style,
      }),
    [normalizedDisabled, normalizedDisableHoverEffects, isInteractive, style],
  );
  const visualIsHovered = visualState.isHovered;
  const visualIsPressed = visualState.isPressed;
  const currentStateContainerStyle = useMemo<CSSProperties | undefined>(
    (): CSSProperties | undefined =>
      resolveCurrentStateContainerStyle({
        isHovered: visualIsHovered,
        isPressed: visualIsPressed,
        stateContainerStyle,
      }),
    [stateContainerStyle, visualIsHovered, visualIsPressed],
  );
  const containerVisualProps = useMemo<ButtonContainerVisualProps>(
    (): ButtonContainerVisualProps =>
      resolveButtonContainerVisualProps({
        config,
        containerPreset,
        backgroundOpacity,
        borderColor,
        borderOpacity,
        backdropBlur,
        visualState,
      }),
    [
      backdropBlur,
      backgroundOpacity,
      borderColor,
      borderOpacity,
      config,
      containerPreset,
      visualState,
    ],
  );
  const presetContainerStyle = config.containerStyle;
  const resolvedContainerStyle = useMemo<CSSProperties>(
    (): CSSProperties =>
      resolveContainerStyle({
        presetContainerStyle,
        containerStyle,
        currentStateContainerStyle,
      }),
    [containerStyle, currentStateContainerStyle, presetContainerStyle],
  );
  const visualGlossy = visualState.glossy;
  const styleModel = useMemo<ButtonStyleModel>(
    (): ButtonStyleModel =>
      resolveButtonStyleModel({
        buttonStyle,
        containerVisualProps,
        resolvedContainerStyle,
        glossy: visualGlossy,
      }),
    [buttonStyle, containerVisualProps, resolvedContainerStyle, visualGlossy],
  );

  return useMemo<ButtonRenderModel>(
    (): ButtonRenderModel =>
      resolveButtonRenderModel({
        contentModel,
        layoutModel,
        styleModel,
        ariaLabel,
        label,
        href,
        isInteractive,
        pressState,
        clickInvestigationHandlers,
        containerPreset,
        radius: normalizedRadius,
        gap: normalizedGap,
        preserveStyle,
        isLoading: normalizedIsLoading,
        className,
        beforeContent,
      }),
    [
      ariaLabel,
      beforeContent,
      className,
      clickInvestigationHandlers,
      containerPreset,
      contentModel,
      href,
      isInteractive,
      label,
      layoutModel,
      normalizedGap,
      normalizedIsLoading,
      normalizedRadius,
      preserveStyle,
      pressState,
      styleModel,
    ],
  );
}
